import React, { useEffect, useState } from "react";
import { Form, Select, Switch } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { WeightHint } from "@fitness-app/data-models/entities/TrainingProgram";

export interface TraineeWorkoutSettingsFormModel {
  weightHint: WeightHint;
  applyHintToRepeats: boolean;
}

interface TraineeWorkoutSettingsFormProps {
  formController?: FormInstance<TraineeWorkoutSettingsFormModel>;
  onSubmit: (formData: TraineeWorkoutSettingsFormModel) => void;
  model: TraineeWorkoutSettingsFormModel;
}

const TraineeWorkoutSettingsForm = ({ model, onSubmit, formController }: TraineeWorkoutSettingsFormProps) => {
  const { t } = useTranslation(["workouts"]);
  const [selectedHint, setSelectedHint] = useState(model.weightHint);

  useEffect(() => {
    formController?.setFieldsValue({ ...model });
    setSelectedHint(model.weightHint);
  }, [model]);

  const hintIsOff = selectedHint === WeightHint.DEFAULT;

  return (
    <Form<TraineeWorkoutSettingsFormModel>
      name="workout-settings-form"
      layout="vertical"
      form={formController}
      onFinish={(formData) =>
        // A repeat hint without an active weight hint would be stored but never applied.
        onSubmit({ ...formData, applyHintToRepeats: hintIsOff ? false : formData.applyHintToRepeats })
      }
      initialValues={model}
    >
      <Form.Item name="weightHint" label={t("hint.exercisesHints")}>
        <Select<WeightHint>
          onChange={setSelectedHint}
          options={[WeightHint.DEFAULT, WeightHint.LATEST, WeightHint.MAX_VALUE].map((value) => ({
            value,
            label: t(`hint.${value.toLowerCase()}`),
          }))}
        />
      </Form.Item>

      <Form.Item
        name="applyHintToRepeats"
        label={t("hint.applyToRepeats")}
        valuePropName="checked"
        extra={hintIsOff ? t("hint.applyToRepeatsRequiresHint") : t("hint.applyToRepeatsDescription")}
      >
        <Switch disabled={hintIsOff} />
      </Form.Item>
    </Form>
  );
};

export default TraineeWorkoutSettingsForm;
